import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { createUser, deleteUser, fetchUsers, updateUser } from '../lib/queries.ts';
import type { ManagedUser } from '../lib/types.ts';
import { useAuthUser } from '../lib/use-auth.ts';

export function UsersPage(): React.ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const currentUser = useAuthUser();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<ManagedUser | null>(null);

  const usersQuery = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const users = usersQuery.data?.users ?? [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('users.title')}</h1>
          <div className="sub">{t('users.subtitle', { count: users.length })}</div>
        </div>
        <div className="filters">
          <button className="primary" onClick={() => setShowCreate(true)}>
            {t('users.create')}
          </button>
        </div>
      </div>

      <div className="section">
        <div className="section-body" style={{ padding: 12 }}>
          {users.map((u) => (
            <UserCard
              key={u.id}
              user={u}
              isSelf={u.id === currentUser?.id}
              onEdit={() => setEditing(u)}
              onDelete={async () => {
                if (confirm(t('users.deleteConfirm', { email: u.email }))) {
                  try {
                    await deleteUser(u.id);
                    invalidate();
                  } catch (e) {
                    alert(e instanceof Error ? e.message : String(e));
                  }
                }
              }}
            />
          ))}
        </div>
      </div>

      {showCreate && (
        <UserFormDialog
          mode="create"
          onClose={() => setShowCreate(false)}
          onSuccess={() => {
            setShowCreate(false);
            invalidate();
          }}
        />
      )}

      {editing && (
        <UserFormDialog
          mode="edit"
          user={editing}
          onClose={() => setEditing(null)}
          onSuccess={() => {
            setEditing(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}

interface UserCardProps {
  user: ManagedUser;
  isSelf: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function UserCard({ user, isSelf, onEdit, onDelete }: UserCardProps): React.ReactElement {
  const { t } = useTranslation();
  const created = new Date(user.createdAt).toLocaleDateString();

  return (
    <div className="account-card">
      <div className="ico tg">{user.role === 'admin' ? '🛡' : '👤'}</div>
      <div className="body">
        <div className="name">
          {user.email}
          {isSelf && (
            <span style={{ marginLeft: 8, color: 'var(--neon)', fontSize: 11 }}>
              ({t('users.you')})
            </span>
          )}
        </div>
        <div className="meta">{t('users.createdAt', { date: created })}</div>
      </div>
      <div className="right-col">
        <span className={`pill ${user.role === 'admin' ? 'live' : 'idle'}`}>
          {t(`users.roles.${user.role}`)}
        </span>
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button
            onClick={onEdit}
            style={{
              background: 'transparent',
              border: '1px solid var(--line-strong)',
              color: 'var(--text-2)',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 11,
              cursor: 'pointer',
            }}
          >
            ✎ {t('common.edit')}
          </button>
          <button
            onClick={onDelete}
            disabled={isSelf}
            title={isSelf ? t('users.you') : undefined}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,61,110,0.3)',
              color: 'var(--hot)',
              padding: '4px 10px',
              borderRadius: 6,
              fontSize: 11,
              cursor: isSelf ? 'not-allowed' : 'pointer',
              opacity: isSelf ? 0.4 : 1,
            }}
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}

interface UserFormDialogProps {
  mode: 'create' | 'edit';
  user?: ManagedUser;
  onClose: () => void;
  onSuccess: () => void;
}

function UserFormDialog(props: UserFormDialogProps): React.ReactElement {
  const { t } = useTranslation();
  const [email, setEmail] = useState(props.user?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'moderator'>(props.user?.role ?? 'moderator');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async () => {
      if (props.mode === 'edit' && props.user) {
        const body: { role?: 'admin' | 'moderator'; password?: string } = { role };
        if (password) body.password = password;
        await updateUser(props.user.id, body);
      } else {
        await createUser({ email, password, role });
      }
    },
    onSuccess: props.onSuccess,
    onError: (e: Error) => setError(e.message),
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 200,
      }}
      onClick={props.onClose}
    >
      <div
        style={{
          width: 460,
          background: 'var(--bg-card)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          padding: 24,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ fontSize: 18, marginBottom: 16 }}>
          {props.mode === 'create'
            ? t('users.form.createTitle')
            : t('users.form.editTitle', { email: props.user?.email })}
        </h2>
        <form onSubmit={handleSubmit}>
          {props.mode === 'create' && (
            <div className="form-row">
              <label>{t('users.form.email')}</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('users.form.emailPlaceholder')}
                autoFocus
              />
            </div>
          )}
          <div className="form-row">
            <label>{props.mode === 'create' ? t('users.form.password') : t('users.form.newPassword')}</label>
            <input
              type="password"
              required={props.mode === 'create'}
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={
                props.mode === 'create'
                  ? t('users.form.passwordPlaceholder')
                  : t('users.form.newPasswordPlaceholder')
              }
            />
          </div>
          <div className="form-row">
            <label>{t('users.form.role')}</label>
            <select value={role} onChange={(e) => setRole(e.target.value as 'admin' | 'moderator')}>
              <option value="moderator">{t('users.roles.moderator')}</option>
              <option value="admin">{t('users.roles.admin')}</option>
            </select>
          </div>

          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: 'rgba(255,61,110,0.1)',
                border: '1px solid rgba(255,61,110,0.3)',
                borderRadius: 6,
                color: 'var(--hot)',
                fontSize: 12,
                marginBottom: 12,
              }}
            >
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={props.onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="primary" disabled={mutation.isPending}>
              {mutation.isPending
                ? t('users.form.submitting')
                : props.mode === 'create'
                  ? t('users.form.submitCreate')
                  : t('users.form.submitEdit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
